import React, { useState, useMemo, useEffect } from 'react';
import ReportLayout, { DateRangePicker } from './components/ReportLayout';
import { CreditCard, Building2, ChevronDown } from 'lucide-react';
import useFinancialReports from '@/hooks/useFinancialReports';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
const PERIOD_MAP = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this_week',
  thisMonth: 'this_month',
  lastMonth: 'last_month',
};

export default function PaymentAnalysisReport() {
  const { hasPermission } = useAuth();

if (!hasPermission(P.REPORTS.FINANCIAL.SCREEN)) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Access Denied
        </h3>
        <p className="text-sm text-slate-500">
          You don't have permission to view this page.
        </p>
      </div>
    </div>
  );
}

  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedPreset, setSelectedPreset] = useState('today');
  const [isCustomDate, setIsCustomDate] = useState(false);

  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');

  const sqlPeriod = selectedPreset
    ? PERIOD_MAP[selectedPreset] ?? null
    : null;

  const [selectedBranchId, setSelectedBranchId] = useState(null); // null = All Branches

  const {
    data,
    summary,
    branchSummary,
    loading,
    setFilters,
    refreshPaymentAnalysis,
  } = useFinancialReports({
    period: sqlPeriod,
    startDate: sqlPeriod ? null : startDate,
    endDate: sqlPeriod ? null : endDate,
    offset: 0,
    limit: pageSize,
  });


  useEffect(() => {
    if (!isCustomDate) {
      setFilters(prev => ({
        ...prev,
        period: sqlPeriod,
        startDate: null,
        endDate: null,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
      }));
    }
  }, [sqlPeriod, currentPage, pageSize, isCustomDate, setFilters]);


  const handleApplyFilters = () => {
    setFilters(prev => ({
      ...prev,
      period: null,
      startDate,
      endDate,
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
    }));
  };

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;


  const exportToCsv = (filename, columns, rows) => {
    if (!rows?.length) return;
    const headers = columns.map(c => `"${c.label}"`).join(',');
    const csvRows = rows.map(r =>
      columns.map(c => r[c.key] ?? '').join(',')
    );
    const blob = new Blob([headers, '\n', ...csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handleExport = () => {
    exportToCsv('payment-analysis.csv', columns, data);
  };

  const summaryCards = useMemo(() => [
    {
      title: 'Total Collection',
      value: `₹${(summary?.totalCollection ?? 0).toLocaleString()}`,
      icon: CreditCard,
    },
    {
      title: 'Cash',
      value: `₹${(summary?.cashTotal ?? 0).toLocaleString()}`,
      icon: CreditCard,
    },
    {
      title: 'UPI',
      value: `₹${(summary?.upiTotal ?? 0).toLocaleString()}`,
      icon: CreditCard,
    },
//     {
//       title: 'Card',
//       value: `₹${(summary?.cardTotal ?? 0).toLocaleString()}`,
//       icon: CreditCard,
//     },
  ], [summary]);

  const columns = [
    {
    key: 'date', label: 'Date', sortable: true,
    render: (v) =>
      v
        ? new Date(v).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : '—',
  },
    { key: 'orders', label: 'Orders',sortable: true, },
    { key: 'total', label: 'Total' ,sortable: true,},
    { key: 'cash', label: 'Cash' ,sortable: true,},
    { key: 'cashOrders', label: 'Cash Orders' ,sortable: true,},
    { key: 'upi', label: 'UPI' ,sortable: true,},
    { key: 'upiOrders', label: 'UPI Orders' ,sortable: true,},
  //{ key: 'card', label: 'Card', sortable: true, align: 'right', render: (v) => `₹${v.toLocaleString()}` },
  //{ key: 'cardOrders', label: 'Card Orders', sortable: true, align: 'right' },
  ];


  const filters = (
    <div className="flex items-end gap-3 flex-wrap">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        selectedPreset={selectedPreset}
        onPresetSelect={(preset) => {
          setSelectedPreset(preset);
          setIsCustomDate(false);
        }}
        onStartDateChange={(d) => {
          setStartDate(d);
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}
        onEndDateChange={(d) => {
          setEndDate(d);
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}
      />

      <button
        onClick={handleApplyFilters}
        disabled={!isCustomDate}
        className={`px-5 py-2 rounded-lg text-white ${
          isCustomDate
            ? 'bg-teal-500 hover:bg-teal-600'
            : 'bg-gray-300 cursor-not-allowed'
        }`}
      >
        Apply Filters
      </button>

      {branchOptions.length > 0 && (
        <div className="relative">
          <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <select
            value={selectedBranchId ?? ''}
            onChange={(e) => {
              const val = e.target.value || null;
              setSelectedBranchId(val);
              setCurrentPage(1);
              setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
            }}
            className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
          >
            <option value="">All Branches</option>
            {branchOptions.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      )}
    </div>
  );
   //SORTING
const sortedData = useMemo(() => {
  if (!data || !sortColumn) return data;

  const sorted = [...data];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (sortColumn === 'date') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === 'asc'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
  });

  return sorted;
}, [data, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in date (convert to string first)
    if (String(row.date || '').toLowerCase().includes(query)) return true;
    // Search in numeric fields
    const numericFields = ['orders', 'total', 'cash', 'cashOrders', 'upi', 'upiOrders'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Payment Analysis"
      description="Breakdown of payments by mode - Cash, UPI, Card"
      icon={CreditCard}
      exportPermission={P.REPORTS.FINANCIAL.PAYMENT_EXPORT}
      color="emerald"
      category="Financial"
      categoryId="financial"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      pagination={{
        currentPage,
        totalPages: 1,
        totalItems: filteredData?.length ?? 0,
        pageSize,
        onPageChange: setCurrentPage,
        onPageSizeChange: setPageSize,
      }}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
    onSort={(column) => {
    let nextDirection;

    if (sortColumn === column) {
      nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      nextDirection = 'desc';
    }

    setSortColumn(column);
    setSortDirection(nextDirection);
    }}
      onExport={(format) => {
        if (format === 'csv') handleExport();
      }}
      onRefresh={refreshPaymentAnalysis}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by date, amount..."
    />
  );
}
